import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionBar,
  Button,
  Card,
  Icon,
  Notice,
  SignerAvatars,
  iconCheck,
  iconCopy,
  iconShare,
  useSnackbar,
} from '@sauda/ui/kit';
import { useAuth } from '../../auth/AuthContext.js';
import { useChrome } from '../../app/chrome.js';
import { api, dealErrorText } from '../../lib/api.js';
import { initials } from '../../lib/initials.js';
import { formatIst } from '../../lib/time.js';
import { memoShareText, whatsappLink } from '../../lib/share-text.js';
import type { DealDetailView, MemoLinks, MemoStatusView } from '../../lib/types.js';
import { AFTER_MEMO } from './stages.js';

const PARTIES = ['tenant', 'owner', 'broker'] as const;

export function memoQueryKey(dealId: string) {
  return ['memo', dealId];
}

/**
 * The memo: three signers with who has signed, a WhatsApp share and a copy for each link, and
 * new links if one was lost. It checks again every few seconds while signatures are out, so a
 * tick appears as it happens.
 */
export function MemoCard({
  deal,
  onFullySigned,
  dock,
}: {
  deal: DealDetailView;
  onFullySigned: () => void;
  /** Where the docked action goes: the end of the screen, so it stays above the bottom bar. */
  dock: HTMLElement | null;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [links, setLinks] = useState<MemoLinks | null>(null);
  const [error, setError] = useState<string | null>(null);
  const waiting = deal.state === 'token_recorded';
  const memo = useQuery({
    queryKey: memoQueryKey(deal.id),
    queryFn: () => api<MemoStatusView>(`/deals/${deal.id}/memo`),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (waiting && data !== undefined && data.versions.length > 0) return 4000;
      // The signed copy is made by the worker a moment after the last signature.
      return AFTER_MEMO.includes(deal.state) &&
        data !== undefined &&
        data.versions.length > 0 &&
        !data.versions.some((v) => v.hasPdf)
        ? 1500
        : false;
    },
  });
  const send = useMutation({
    mutationFn: () => api<MemoLinks>(`/deals/${deal.id}/memo`, { method: 'POST', body: {} }),
    onSuccess: async (result) => {
      setLinks(result);
      setError(null);
      await cache.invalidateQueries({ queryKey: memoQueryKey(deal.id) });
    },
    onError: (e) => setError(dealErrorText(e, t)),
  });
  const brokerLink = useMutation({
    mutationFn: () =>
      api<{ link: string }>(`/deals/${deal.id}/memo/broker-link`, { method: 'POST', body: {} }),
    onSuccess: (result) => {
      // In the same tab: the signing page sends the broker back to this deal when they are done.
      window.location.assign(`${result.link}?back=${encodeURIComponent(`/deals/${deal.id}`)}`);
    },
    onError: (e) => setError(dealErrorText(e, t)),
  });
  const renew = useMutation({
    mutationFn: () => api<MemoLinks>(`/deals/${deal.id}/memo/links`, { method: 'POST', body: {} }),
    onSuccess: (result) => setLinks(result),
    onError: (e) => setError(dealErrorText(e, t)),
  });

  const data = memo.data;
  const sent = (data?.versions.length ?? 0) > 0;
  const signed = (data?.signers ?? []).filter((s) => s.signedAt !== null).length;
  const everyone = sent && data !== undefined && data.signers.length === 3 && signed === 3;
  // When the last signature lands while this screen is open, say so once.
  const was = useRef(false);
  useEffect(() => {
    if (everyone && !was.current && waiting) onFullySigned();
    was.current = everyone;
  }, [everyone, waiting, onFullySigned]);

  if (deal.state === 'open') return null;
  const partyPhone = (party: string): string | null =>
    party === 'tenant' ? deal.client.phone : party === 'owner' ? deal.owner.phone : null;
  const brokerSigned =
    data?.signers.some((s) => s.party === 'broker' && s.signedAt !== null) ?? false;
  const dockButton = !waiting ? null : !sent ? (
    <Button
      size="lg"
      loading={send.isPending}
      disabled={chrome.readOnly}
      onClick={() => send.mutate()}
    >
      {t('deal.sendMemo')}
    </Button>
  ) : brokerSigned ? null : (
    <Button
      size="lg"
      loading={brokerLink.isPending}
      disabled={chrome.readOnly}
      onClick={() => brokerLink.mutate()}
    >
      {t('deal.signAsBroker')}
    </Button>
  );
  return (
    <>
      <Card data-testid="memo-card">
        <h2 className="k-t-title-sm">
          {sent
            ? `${t('deal.memoVersion', { version: data?.versions.at(-1)?.version ?? 1 })} · ${t('deal.signedCount', { signed, total: data?.signers.length ?? 3 })}`
            : t('deal.memo')}
        </h2>
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        {sent && data !== undefined ? (
          <>
            <SignerAvatars
              signedLabel={t('deal.signedWord')}
              waitingLabel={t('deal.waitingWord')}
              signers={[...data.signers]
                .sort(
                  (x, y) => PARTIES.indexOf(x.party as never) - PARTIES.indexOf(y.party as never),
                )
                .map((s) => ({
                  key: s.party,
                  name: s.name,
                  initials: initials(s.name),
                  party: t(`deals.party_${s.party}`),
                  signed: s.signedAt !== null,
                }))}
            />
            <ul className="k-sr-only">
              {data.signers.map((s) => (
                <li key={s.party} data-testid={`signer-${s.party}`}>
                  {t(`deals.party_${s.party}`)}: {s.name} ·{' '}
                  {s.signedAt === null ? t('deals.notSigned') : t('deals.signed')}
                </li>
              ))}
            </ul>
            {signed === 0 ? null : (
              <p className="k-t-label-sm k-muted">
                {[...data.signers]
                  .filter((s) => s.signedAt !== null)
                  .sort(
                    (x, y) => PARTIES.indexOf(x.party as never) - PARTIES.indexOf(y.party as never),
                  )
                  .map((s) =>
                    t('deal.signedAt', {
                      party: t(`deals.party_${s.party}`),
                      time: formatIst(s.signedAt as string),
                    }),
                  )
                  .join(' · ')}
              </p>
            )}
          </>
        ) : null}
        {links === null ? null : (
          <div className="today-section">
            <Notice tone="info">{t('deals.linksHelp')}</Notice>
            {PARTIES.map((party) => (
              <div key={party} className="link-row">
                <span className="k-t-label k-minw-label">{t(`deals.party_${party}`)}</span>
                <a
                  className="k-btn k-btn--filled k-btn--md k-layer k-t-label-lg"
                  href={whatsappLink(
                    memoShareText({
                      party: t(`deals.party_${party}`),
                      propertyTitle: deal.propertyTitle ?? '',
                      agency: auth.current?.agencyName ?? '',
                      link: links.links[party],
                    }),
                    partyPhone(party),
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon icon={iconShare} size={18} />
                  {t('deal.shareWhatsapp')}
                </a>
                <Button
                  variant="tonal"
                  onClick={() => {
                    void navigator.clipboard?.writeText(links.links[party]);
                    snack({ text: t('deal.linkCopied', { party: t(`deals.party_${party}`) }) });
                  }}
                  aria-label={t('deal.copyLinkFor', { party: t(`deals.party_${party}`) })}
                >
                  <Icon icon={iconCopy} size={18} />
                  {t('deal.copyLink')}
                </Button>
                <label className="k-sr-only" htmlFor={`link-${party}`}>
                  {t(`deals.party_${party}`)}
                </label>
                <input
                  id={`link-${party}`}
                  className="k-sr-only"
                  readOnly
                  value={links.links[party]}
                />
              </div>
            ))}
          </div>
        )}
        <div className="chips-row">
          {waiting && sent ? (
            <Button
              variant="tonal"
              loading={send.isPending}
              disabled={chrome.readOnly}
              onClick={() => send.mutate()}
            >
              {t('deals.sendAgain')}
            </Button>
          ) : null}
          {waiting && sent ? (
            <Button
              variant="tonal"
              loading={renew.isPending}
              disabled={chrome.readOnly}
              onClick={() => renew.mutate()}
            >
              {t('deals.newLinks')}
            </Button>
          ) : null}
          {AFTER_MEMO.includes(deal.state) && (data?.versions.some((v) => v.hasPdf) ?? false) ? (
            <Button
              variant="tonal"
              onClick={() =>
                void api<{ url: string | null }>(`/deals/${deal.id}/memo/pdf`).then((r) => {
                  if (r.url !== null) window.open(r.url, '_blank', 'noopener');
                })
              }
            >
              <Icon icon={iconCheck} size={18} />
              {t('deals.downloadMemo')}
            </Button>
          ) : null}
        </div>
      </Card>
      {dock === null || dockButton === null
        ? null
        : createPortal(<ActionBar label={t('deal.actions')}>{dockButton}</ActionBar>, dock)}
    </>
  );
}
